/**
 * Centralized date formatting utilities
 */

/**
 * Converts various date input types to a standardized Date object
 * @param {string|Date|number} input - The date input to convert
 * @returns {Date|null} Date object or null if invalid
 */
const parseDate = (input) => {
  if (!input) return null;

  // If it's already a Date object
  if (input instanceof Date) {
    return isNaN(input.getTime()) ? null : input;
  }

  // If it's a number (timestamp)
  if (typeof input === "number") {
    const date = new Date(input);
    return isNaN(date.getTime()) ? null : date;
  }

  // If it's not a string, try to convert it
  if (typeof input !== "string") {
    input = String(input);
  }

  // Handle string inputs
  let date;

  if (input.endsWith("Z")) {
    // Already has UTC indicator - parse directly
    date = new Date(input);
  } else if (
    input.includes("T") &&
    !input.includes("Z") &&
    !input.includes("+") &&
    !input.match(/-\d{2}:\d{2}$/)
  ) {
    // ISO format without timezone - treat as UTC
    date = new Date(input + "Z");
  } else if (
    input.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/) &&
    !input.includes("+") &&
    !input.match(/-\d{2}:\d{2}$/)
  ) {
    // Space-separated format (YYYY-MM-DD HH:MM:SS) - treat as UTC
    const utcString = input.replace(" ", "T") + "Z";
    date = new Date(utcString);
  } else {
    date = new Date(input);
  }

  return isNaN(date.getTime()) ? null : date;
};

/**
 * Formats a date string to a full date and time representation
 * @param {string|Date|number} input - The date input to format
 * @returns {string} Formatted date string or "N/A" if invalid
 */
export const formatDate = (input) => {
  const date = parseDate(input);
  if (!date) return "N/A";

  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
};

/**
 * Formats a date string to time only
 * @param {string|Date|number} input - The date input to format
 * @returns {string} Formatted time string or "N/A" if invalid
 */
export const formatTime = (input) => {
  const date = parseDate(input);
  if (!date) return "N/A";

  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit"
  });
};

/**
 * Formats a date string to "Nov 5, 2025 at 5:23 PM" format
 * @param {string|Date|number} input - The date input to format
 * @returns {string} Formatted date string or "N/A" if invalid
 */
export const formatDateWithTime = (input) => {
  const date = parseDate(input);
  if (!date) return "N/A";

  const month = date.toLocaleString("en-US", { month: "short" });
  const day = date.getDate();
  const year = date.getFullYear();
  const time = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit"
  });

  return `${month} ${day}, ${year} at ${time}`;
};

/**
 * Formats a date string for message display:
 * - If the date is today: shows just the time (e.g., "5:23 PM")
 * - If the date is not today: shows full date and time (e.g., "Nov 5, 2025 at 5:23 PM")
 * @param {string|Date|number} input - The date input to format
 * @returns {string} Formatted date string or "N/A" if invalid
 */
export const formatMessageTime = (input) => {
  const date = parseDate(input);
  if (!date) return "N/A";

  // Get today's date in local timezone for comparison
  const today = new Date();
  const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  
  const isToday = messageDate.getTime() === todayDate.getTime();

  if (isToday) {
    return formatTime(input);
  } else {
    return formatDateWithTime(input);
  }
};
